/**
 * "By age" arrangement of the tree.
 *
 * Same circles, same lines, same left-to-right order as the generation layout
 * (layout.ts) — but a circle's *height* comes from its birth year, so higher
 * always means born earlier. That is what makes a family with a wide spread
 * readable: an uncle born in 1962 and his nephew born in 1960 sit at the same
 * height, and a set of siblings twenty-five years apart slopes down the page
 * instead of lining up in a row.
 *
 * Rules:
 *   - everyone sits at their own year — a couple stays side by side, but each
 *     at their own height, so the gap between them is visible too;
 *   - someone without a year is placed from their relatives (parents + 28,
 *     children − 28, partner), and failing that from their generation;
 *   - a child is always drawn at least MIN_GAP_YEARS below their parents, so a
 *     line never runs upwards (bad data or an estimate may otherwise do that);
 *   - people who end up at the same height and place are pushed apart
 *     sideways only (treeRelax.relaxBands), never up or down — the height is
 *     the information.
 */
import { children, parents, partners, type FamilyGraph } from '../../domain/relationships';
import type { Relationship, UUID } from '../../domain/types';
import { AVATAR_DY, AVATAR_R, boundsOfNodes, buildEdges, layoutTree, NODE_H, type LaidOutNode, type TreeLayout } from './layout';
import { relaxBands, type BandNode } from './treeRelax';

/** Vertical pixels per year of age. 12 → a 100-year family is 1200px tall, a generation (~28 y) ≈ 336px. */
export const PX_PER_YEAR = 12;
/** A child is drawn at least this many years below their parents. */
export const MIN_GAP_YEARS = 14;
/** Typical parent → child distance, used to place people whose year is unknown. */
const GENERATION_YEARS = 28;
/** Gap between circles pushed apart sideways (same as the generation layout's live push). */
const REST_GAP = 14;

export function layoutTreeByAge(graph: FamilyGraph, visibleIds: UUID[], relationships: Relationship[], birthYear: (id: UUID) => number | null): TreeLayout {
  // 1. Left-to-right order, couples, and generation levels come from the ordinary layout.
  const base = layoutTree(graph, visibleIds, relationships, birthYear);
  if (!base.nodes.size) return base;
  const ids = [...base.nodes.keys()];
  const idSet = new Set(ids);

  // 2. A year for everyone with one.
  const year = new Map<UUID, number>();
  const known = new Set<UUID>();
  for (const id of ids) {
    const y = birthYear(id);
    if (y !== null && Number.isFinite(y)) { year.set(id, y); known.add(id); }
  }

  // 3. Estimate the rest from relatives, spreading outwards from what is known:
  //    a partner's year, parents + a generation, children − a generation.
  const inView = (list: UUID[]) => list.filter((x) => idSet.has(x));
  for (let pass = 0; pass < ids.length + 2; pass++) {
    let changed = false;
    for (const id of ids) {
      if (year.has(id)) continue;
      const guesses: number[] = [];
      for (const s of inView(partners(graph, id))) if (year.has(s)) guesses.push(year.get(s)!);
      for (const p of inView(parents(graph, id))) if (year.has(p)) guesses.push(year.get(p)! + GENERATION_YEARS);
      for (const c of inView(children(graph, id))) if (year.has(c)) guesses.push(year.get(c)! - GENERATION_YEARS);
      if (guesses.length) { year.set(id, guesses.reduce((a, b) => a + b, 0) / guesses.length); changed = true; }
    }
    if (!changed) break;
  }
  // Still unknown (no dated relatives at all): by generation, relative to the oldest known year.
  const levelOf = (id: UUID) => base.nodes.get(id)!.level;
  const minKnownYear = known.size ? Math.min(...[...known].map((id) => year.get(id)!)) : 1900;
  const minKnownLevel = known.size ? Math.min(...[...known].map(levelOf)) : 0;
  for (const id of ids) if (!year.has(id)) year.set(id, minKnownYear + (levelOf(id) - minKnownLevel) * GENERATION_YEARS);

  // 4. Never draw a child above (or too close under) a parent: push down only, so known years stay put unless the data contradicts itself.
  for (let pass = 0; pass < ids.length + 2; pass++) {
    let changed = false;
    for (const id of ids) {
      for (const p of inView(parents(graph, id))) {
        const floor = year.get(p)! + MIN_GAP_YEARS;
        if (year.get(id)! < floor) { year.set(id, floor); changed = true; }
      }
    }
    if (!changed) break;
  }

  // 5. Positions: x as in the generation layout, y from the year. Then push same-height neighbours apart sideways.
  const minYear = Math.min(...year.values());
  const yOf = (y: number) => (y - minYear) * PX_PER_YEAR + NODE_H / 2;
  const band: BandNode[] = ids.map((id) => {
    const n = base.nodes.get(id)!;
    const y = yOf(year.get(id)!);
    return { id, x: n.x, y, halfW: AVATAR_R, top: y + AVATAR_DY - AVATAR_R, bottom: y + NODE_H / 2 };
  });
  const restX = relaxBands(band, REST_GAP);
  const nodes = new Map<UUID, LaidOutNode>();
  for (const b of band) nodes.set(b.id, { ...base.nodes.get(b.id)!, x: restX.get(b.id) ?? b.x, y: b.y });

  // 6. Edges, bounds and the decade marks (drawn as faint lines; the labels stick to the screen edge — see TreeScreen).
  const edges = buildEdges(graph, nodes, relationships);
  const bounds = boundsOfNodes(nodes)!;
  const maxYear = Math.max(...year.values());
  const yearMarks: Array<{ year: number; y: number }> = [];
  const firstDecade = Math.floor(minYear / 10) * 10;
  for (let y = firstDecade; y <= Math.ceil(maxYear / 10) * 10; y += 10) yearMarks.push({ year: y, y: yOf(y) + AVATAR_DY });
  const minX = bounds.minX;
  const minY = Math.min(bounds.minY, yearMarks[0]?.y ?? bounds.minY) - 12;
  const maxY = Math.max(bounds.minY + bounds.height, yearMarks[yearMarks.length - 1]?.y ?? 0) + 12;
  return {
    nodes,
    edges,
    width: bounds.width,
    height: maxY - minY,
    levels: base.levels,
    minX,
    minY,
    yearMarks,
  };
}
